import React, { useEffect, useState, useRef } from "react";
import { MDBContainer, MDBIcon, MDBBtn } from "mdb-react-ui-kit";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import LegislatorTableHeader from "./LegislatorTableHeader";
import { toTitleCase, formatEmail } from "../../utils/stringOperations";
import { exportToCSV, exportToPDF } from "../../utils/exportDatatable";
import "../../scss/datatable.scss";
import ExportProgress from "../common/ExportProgress";
import { getAllLegislatorsFromWordpress } from "../../utils/legislatorsOperations";

function LegislatorTable({
  filterSideNavIsOpen,
  setFilterSideNavIsOpen,
  userSelectedSession,
  activeSession,
  userSelectedLegislature,
  userSelectedStatus,
  userSelectedChamber,
  userSelectedParty,
}) {
  const [legislators, setLegislators] = useState([]);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    firstName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    lastName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    telephone: { value: null, matchMode: FilterMatchMode.CONTAINS },
    city: { value: null, matchMode: FilterMatchMode.CONTAINS },
    county: { value: null, matchMode: FilterMatchMode.CONTAINS },
    title: { value: null, matchMode: FilterMatchMode.CONTAINS },
    chamber: { value: null, matchMode: FilterMatchMode.EQUALS },
    districtAbbreviated: { value: null, matchMode: FilterMatchMode.CONTAINS },
    politicalParty: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [visibleColumns, setVisibleColumns] = useState({
    portrait: true,
    lastName: true,
    firstName: true,
    email: false,
    telephone: true,
    addressLine1: false,
    addressLine2: false,
    city: false,
    state: false,
    postalCode: false,
    county: true,
    title: true,
    chamber: true,
    district: true,
    politicalParty: true,
    status: true,
  });
  const [loading, setLoading] = useState(false);
  const [
    indexOfCurrentFileGettingPrepared,
    setIndexOfCurrentFileGettingPrepared,
  ] = useState(0);
  const [exportProgressVisible, setExportProgressVisible] = useState(false);
  const [totalLegislatorsToExport, setTotalLegislatorsToExport] = useState(0);

  const dt = useRef(null);

  useEffect(() => {
    const loadLegislators = async () => {
      try {
        setLoading(true);
        const allLegislatorsFromWordpress = await getAllLegislatorsFromWordpress();
        const filteredLegislators = allLegislatorsFromWordpress
        .map(legislator => ({
          ...legislator,
          status: legislator.isActive ? "Active" : "Former",
          title: legislator.hasLeadershipPosition ? legislator.leadershipPosition : "",
          email: legislator.emailAddress || "",
          telephone: legislator.phoneNumber || "",
        }))
        .filter(legislator => {
          const legislatorChamber = legislator.chamber.toUpperCase();
          const legislatorParty = legislator.politicalParty.toUpperCase();

          return (
            (!userSelectedStatus || legislator.status === userSelectedStatus) &&
            (userSelectedChamber.length === 0 || userSelectedChamber.includes(legislatorChamber)) &&
            (userSelectedParty.length === 0 || userSelectedParty.includes(legislatorParty))
          );
        });

        setLegislators(filteredLegislators);
      } catch (error) {
        console.error("Error fetching legislators:", error);
      } finally {
        setLoading(false);
      }
    };
    loadLegislators();
  }, [
    userSelectedLegislature,
    userSelectedStatus,
    userSelectedChamber,
    userSelectedParty,
  ]);

  const handleRowClick = (event) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const newUrl = `${baseUrl}#/legislator/${event.data.lawsId}`;
    window.open(newUrl, "_blank");
  };

  const globalFilterFields = Object.entries(visibleColumns)
    .filter(([_, visible]) => visible)
    .map(([field]) => field)
    .filter((field) => field !== "portrait"); // Skip image from global search

  const chamberFilterOptions = [
    { label: "House", value: "house" },
    { label: "Senate", value: "senate" },
  ];

  const statusFilterOptions = [
    { label: "Active", value: "Active" },
    { label: "Former", value: "Former" },
  ];

  const formatPhoneNumber = (phoneNumber) => {
    const cleaned = ("" + (phoneNumber || "")).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber || "";
  };

  const exportData = async (format) => {
    try {
      setTotalLegislatorsToExport(legislators.length + 1);
      setIndexOfCurrentFileGettingPrepared(0);
      setExportProgressVisible(true);
      const exportableColumns = Object.entries(visibleColumns)
        .filter(([field, isVisible]) => isVisible && field !== "portrait")
        .map(([field]) => ({
          field,
          header:
            {
              lastName: "Last Name",
              firstName: "First Name",
              email: "Email",
              telephone: "Phone",
              addressLine1: "Address 1",
              addressLine2: "Address 2", 
              city: "City",
              state: "State",
              postalCode: "Zip Code",
              county: "County",
              leaderShipPosition: "Title",
              chamber: "Chamber",
              districtAbbreviated: "District",
              politicalParty: "Party",
              status: "Status",
            }[field] || toTitleCase(field),
        }));

      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      const sanitizedData = await Promise.all(
        legislators.map(async (row, index) => {
          const obj = {};
          await delay(10); // Wait 10ms before processing next record
          setIndexOfCurrentFileGettingPrepared(index);
          exportableColumns.forEach((col) => {
            switch (col.field) {
              case "email":
                obj[col.field] = row.emailAddress || "";
                break;
              case "telephone":
                obj[col.field] = (row.phoneNumber || "").replace(/\D/g, '').replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3');
                break;
              case "addressLine1":
                obj[col.field] = row.addressLine || "";
                break;
              case "addressLine2":
                obj[col.field] = row.optionalAddressLine || "";
                break;
              case "state":
                obj[col.field] = row.stateProvince || "";
                break;
              case "title":
                obj[col.field] = row.hasLeadershipPosition ? row.leadershipPosition : "";
                break;
              case "chamber":
                obj[col.field] = toTitleCase(row[col.field]);
                break;
              case "district":
                obj[col.field] = row.districtAbbreviated || "";
                break;
              case "status":
                obj[col.field] = row.isActive === true ? "Active" : "Former";
                break;
              default:
                obj[col.field] = row[col.field] || "";
            }
          });
          return obj;
        })
      );

      if (format === "csv") {
        exportToCSV("Legislators", exportableColumns, sanitizedData);
      } else if (format === "pdf") {
        await exportToPDF("Legislators", exportableColumns, sanitizedData, {
          centerHeaders: [],
        });
      }
      setIndexOfCurrentFileGettingPrepared(0);
      setExportProgressVisible(false);
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  return (
    <MDBContainer fluid>
      <ExportProgress
        title="Exporting Legislators"
        current={indexOfCurrentFileGettingPrepared}
        total={totalLegislatorsToExport}
        show={exportProgressVisible}
      />
      <LegislatorTableHeader
        columns={Object.keys(visibleColumns).map((field) => ({
          field,
          header: field === "postalCode" ? "Zip Code" : toTitleCase(field),
          label:
            {
              firstName: "First Name",
              lastName: "Last Name",
              telephone: "Phone",
              postalCode: "Zip Code",
              addressLine1: "Address 1",
              addressLine2: "Address 2",
              politicalParty: "Party",
            }[field] || toTitleCase(field),
        }))}
        visibleColumns={visibleColumns}
        onToggleColumn={(field) =>
          setVisibleColumns((prev) => ({
            ...prev,
            [field]: !prev[field],
          }))
        }
        onSearch={(value) => {
          setGlobalFilterValue(value);
          setFilters((prev) => ({
            ...prev,
            global: { value, matchMode: FilterMatchMode.CONTAINS },
          }));
        }}
        setFilterSideNavIsOpen={setFilterSideNavIsOpen}
        filterSideNavIsOpen={filterSideNavIsOpen}
        activeSession={activeSession}
        onExport={exportData}
      />
      <DataTable
        ref={dt}
        value={legislators}
        dataKey="lawsId"
        onRowClick={handleRowClick}
        className="bg-white"
        showGridlines
        stripedRows
        resizableColumns
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="650px"
        sortField="lastName"
        sortOrder={1}
        sortMode="multiple"
        removableSort
        loading={loading}
        filters={filters}
        globalFilter={filters.global.value}
        globalFilterFields={globalFilterFields}
        emptyMessage="No legislators found."
      >
        {visibleColumns.portrait && (
          <Column
            header="Portrait"
            field="portrait"
            body={(rowData) => {
              const hasPhoto = rowData?.photo?.sizes?.avatar;
              const initials = `${rowData?.firstName?.[0] || ""}${
                rowData?.lastName?.[0] || ""
              }`;

              return (
                <div className="round-img round-img-sm mx-auto">
                  {hasPhoto ? (
                    <img
                      alt={`${rowData?.firstName} ${rowData?.lastName}`}
                      src={hasPhoto}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = "none";
                        e.target.parentElement.classList.add("no-photo");
                        e.target.parentElement.setAttribute(
                          "data-initials",
                          initials
                        );
                      }}
                    />
                  ) : (
                    <div className="no-photo">
                      <i className="fa-solid fa-circle-user"></i>
                    </div>
                  )}
                </div>
              );
            }}
            bodyStyle={{ textAlign: "center" }}
            headerClassName="text-center"
          />
        )}
        {visibleColumns.lastName && (
          <Column
            header="Last Name"
            field="lastName"
            sortable
            filter
            filterPlaceholder="Search Last Name"
          />
        )}
        {visibleColumns.firstName && (
          <Column
            header="First Name"
            field="firstName"
            sortable
            filter
            filterPlaceholder="Search First Name"
          />
        )}
        {visibleColumns.email && (
          <Column
            header="Email"
            field="email"
            sortable
            filter
            filterPlaceholder="Search Email"
          />
        )}
        {visibleColumns.telephone && (
          <Column
            header="Phone"
            field="telephone"
            body={(rowData) => formatPhoneNumber(rowData.telephone)}
            sortable
            filter
            filterPlaceholder="Search Phone"
          />
        )}
        {visibleColumns.addressLine1 && (
          <Column header="Address 1" field="addressLine" sortable />
        )}
        {visibleColumns.addressLine2 && (
          <Column header="Address 2" field="optionalAddressLine" sortable />
        )}
        {visibleColumns.city && (
          <Column
            header="City"
            field="city"
            sortable
            filter
            filterPlaceholder="Search City"
          />
        )}
        {visibleColumns.state && (
          <Column header="State" field="stateProvince" sortable />
        )}
        {visibleColumns.postalCode && (
          <Column header="Zip Code" field="postalCode" sortable />
        )}
        {visibleColumns.county && (
          <Column
            header="County"
            field="county"
            sortable
            filter
            filterPlaceholder="Search County"
          />
        )}
        {visibleColumns.title && (
          <Column
            header="Title"
            field="title"
            sortable
            filter
            filterPlaceholder="Search Title"
          />
        )}
        {visibleColumns.chamber && (
          <Column
            header="Chamber"
            field="chamber"
            body={(rowData) => toTitleCase(rowData.chamber)}
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={(options) => (
              <Dropdown
                value={options.value}
                options={chamberFilterOptions}
                onChange={(e) => options.filterCallback(e.value)}
                placeholder="Select Chamber"
                className="p-column-filter"
                style={{ width: "100%" }}
              />
            )}
          />
        )}
        {visibleColumns.district && (
          <Column
            header="District"
            field="districtAbbreviated"
            sortable
            filter
            filterPlaceholder="Search District"
          />
        )}
        {visibleColumns.politicalParty && (
          <Column
            header="Party"
            field="politicalParty"
            sortable
            filter
            body={(rowData) =>
              rowData.politicalParty ? rowData.politicalParty : ""
            }
          />
        )}
        {visibleColumns.status && (
          <Column
            header="Status"
            field="status"
            sortable
            filter
            showFilterMatchModes={false}
            filterElement={(options) => (
              <Dropdown
                value={options.value}
                options={statusFilterOptions}
                onChange={(e) => options.filterCallback(e.value)}
                placeholder="Select Status"
                className="p-column-filter"
                style={{ width: "100%" }}
              />
            )}
          />
        )}
        <Column
          header=""
          field="actions"
          body={(rowData) => (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MDBBtn
                tag="a"
                color="none"
                onClick={() => handleRowClick({ data: rowData })}
                title="View Details"
              >
                <MDBIcon far icon="eye" />
              </MDBBtn>
            </div>
          )}
          bodyStyle={{ textAlign: "center" }}
          headerClassName="text-center"
        />
      </DataTable>
    </MDBContainer>
  );
}

export default LegislatorTable;
